import { encodeLngLatPathToGoogleEncodedPolyline } from "@gpx-spa/geo-adapters";
import type {
  LngLatCoord,
  RoutePathOption,
  RoutePlan,
} from "@gpx-spa/geo-domain";

/** `deviceApiPreview` — full upsert-style JSON (labs/prototypes). `engineV2Create` — body for POST /api/v2/accounts/…/shipments (no id/account_id/window). */
export type ShipmentDeviceApiPayloadMode =
  | "deviceApiPreview"
  | "engineV2Create";

/** Input for building a gpx-device-api–style shipment body (orchestration layer; not a shared package contract). */
export interface ShipmentDeviceApiPayloadInput {
  shipmentId: number;
  accountId: number;
  /** One or more device ids attached to the shipment (convoy). */
  deviceIds: number[];
  name: string;
  carrier?: string | null;
  startAt?: Date | string | null;
  endAt?: Date | string | null;
  /** Same semantics as legacy shipment POST: optional notify offset id; lists may be empty. */
  notifyAt?: number | null;
  notificationEmails?: string[];
  notificationPhoneNumbers?: string[];
  /** Nested stops in device-api write shape (`circle` | `geofence`). */
  stops: Record<string, unknown>[];
  routePlan: RoutePlan;
  selectedByPathId: Record<string, boolean>;
  corridorWidthMeters: number;
  /** Per selected path: simplified centerline `[[lng, lat], …]` for encode + persist (same as map line). */
  pathCoordinatesById?: Record<string, LngLatCoord[]>;
}

/** Stops returned from POST …/shipments (persisted ids + sequence). Used to build route legs with `from_stop_id` / `to_stop_id`. */
export type ShipmentStopIdRow = { id: number; sequence: number };

export function pathStableId(path: RoutePathOption, index: number): string {
  return path.id ?? `path_${index}`;
}

/**
 * Stable id for a path option within a route segment. Segment 0 keeps unprefixed ids (map / UI);
 * further segments use `s{segmentIndex}_…` so ids never collide across legs.
 */
export function pathIdForSegment(
  segmentIndex: number,
  path: RoutePathOption,
  pathIndex: number,
): string {
  const base = pathStableId(path, pathIndex);
  return segmentIndex === 0 ? base : `s${segmentIndex}_${base}`;
}

export function collectPathIds(paths: RoutePathOption[]): string[] {
  return paths.map((p, i) => pathStableId(p, i));
}

function toRouteOptionWriteShape(
  p: RoutePathOption,
  coordinates?: LngLatCoord[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.meta?.provider === "learned") {
    out.name = p.id;
    out.meta = { ...p.meta, provider: "learned" };
  } else {
    if (p.id != null) {
      out.name = p.id;
    }
    if (p.meta && Object.keys(p.meta).length > 0) {
      out.meta = { ...p.meta };
    }
  }
  const c = coordinates ?? p.coordinates;
  if (c && c.length >= 2) {
    out.encoded_polyline = encodeLngLatPathToGoogleEncodedPolyline(c);
  }
  return out;
}

/**
 * Route segments for POST …/shipments/{id}/routes (one request per segment), using persisted stop ids.
 * Call after `POST /api/v2/accounts/…/shipments?include=stops` returns `stops` with `id` and `sequence`.
 */
export function buildShipmentRouteSegmentsFromResolvedStops(
  input: ShipmentDeviceApiPayloadInput,
  stopsFromApi: ShipmentStopIdRow[],
): Record<string, unknown>[] {
  const overrides = input.pathCoordinatesById;
  const selected = input.selectedByPathId;

  const sorted = [...stopsFromApi].sort((a, b) => a.sequence - b.sequence);

  const routes: Record<string, unknown>[] = [];
  const segments = input.routePlan.segments ?? [];

  for (let s = 0; s < segments.length; s++) {
    const segment = segments[s];
    if (!segment) continue;
    const fromId = sorted[segment.fromStopIndex]?.id;
    const toId = sorted[segment.toStopIndex]?.id;
    if (fromId == null || toId == null) continue;

    const paths = segment.paths ?? [];
    const routeOptions: Record<string, unknown>[] = [];
    for (let i = 0; i < paths.length; i++) {
      const p = paths[i]!;
      const id = pathIdForSegment(s, p, i);
      if (!selected[id]) continue;
      const coords = overrides?.[id];
      routeOptions.push(toRouteOptionWriteShape(p, coords));
    }

    routes.push({
      from_stop_id: fromId,
      to_stop_id: toId,
      corridor_width_meters: Math.round(input.corridorWidthMeters),
      route_options: routeOptions,
    });
  }

  return routes;
}

/**
 * Nested `stops`. Preview JSON omits `routes` (they require persisted `from_stop_id` / `to_stop_id`; use follow-up POST …/routes).
 * `engineV2Create` — body for POST /api/v2/accounts/{account}/shipments only; route segments are sent via separate POST …/routes calls.
 */
export function buildShipmentDeviceApiPayload(
  input: ShipmentDeviceApiPayloadInput,
  mode: ShipmentDeviceApiPayloadMode = "deviceApiPreview",
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    device_ids: input.deviceIds,
    name: input.name,
    route_deviation_enabled: true,
    stops: input.stops,
  };

  if (mode === "deviceApiPreview") {
    base.id = input.shipmentId;
    base.account_id = input.accountId;
  }

  if (input.carrier != null && String(input.carrier).length > 0) {
    base.carrier = input.carrier;
  }
  if (mode === "deviceApiPreview") {
    if (input.startAt != null) {
      base.start_at = new Date(input.startAt).toISOString();
    }
    if (input.endAt != null) {
      base.end_at = new Date(input.endAt).toISOString();
    }
  }

  base.notify_at = input.notifyAt ?? null;
  base.notification_emails = input.notificationEmails ?? [];
  base.notification_phone_numbers = input.notificationPhoneNumbers ?? [];

  if (mode === "deviceApiPreview") {
    return {
      ...base,
      routes: [],
    };
  }

  return base;
}
